/** SUNTRONIX ONE — Customer-Owned Stock (custodian warehouse).
 *  Store-incharge inward + view. Owner stays the customer; Suntronix is custodian.
 */
import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AsyncCombobox } from "@/components/app/AsyncCombobox";
import { CustomerEntitySelector, PARENT_ENTITY } from "@/components/app/CustomerEntitySelector";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PackagePlus, Loader2, Warehouse, ShieldCheck, X, Info, Tag, ScanLine } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { UnitEditDialog } from "@/components/warehouse/UnitEditDialog";
import { useInvAccess, RowActions, ReasonDialog, errMsg } from "@/components/warehouse/InvEditKit";
import { CustomerStockBoard, LocationsTab, BackfillTab, StockTakeTab, LabelSettings, WarehouseSearch, BulkLabels } from "@/pages/operations/WarehouseTools";

const TABS = [["stock", "Stock"], ["board", "Customer stock board"], ["locations", "Locations"], ["backfill", "Historical backfill"], ["count", "Stock take"], ["search", "Search"]];

const searchCustomers = async (q) => {
  const { data } = await api.get("/master-companies", { params: { q, limit: 25 } });
  return (data.items || []).map((c) => ({
    value: c.id,
    label: c.name || c.legal_name || c.id,
    sub: [c.code, c.country].filter(Boolean).join(" · "),
  }));
};

export default function CustomerOwnedStock() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [cust, setCust] = useState("");           // active customer filter (id)
  const [custLabel, setCustLabel] = useState(""); // its display name
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_id: "", customer_name: "", subsidiary_id: "", subsidiary_name: "", owning_entity_id: "", owning_entity_name: "", owning_entity_type: "", procured_from_vendor: "", product_name: "", brand: "", sku: "", tracking_type: "SERIAL", serials: "", qty: "", warehouse: "", zone: "", rack: "", bin: "", condition: "NEW", dep_stock: "NO", quotation: "", order: "", customer_po: "" });
  const [busy, setBusy] = useState(false);
  const [editU, setEditU] = useState(null);
  const [delU, setDelU] = useState(null);
  const access = useInvAccess();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "stock";

  const load = useCallback(() => {
    const c = cust ? `?customer_id=${encodeURIComponent(cust)}` : "";
    api.get(`/customer-stock/summary${c}`).then(({ data }) => setSummary(data)).catch(() => {});
    api.get(`/customer-stock/list${c}`).then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, [cust]);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Subsidiaries (legal entities) scoped to the customer picked in the form.
  const pickCustomer = (id, item) => {
    setF((s) => ({ ...s, customer_id: id, customer_name: item?.label || "",
      subsidiary_id: "", subsidiary_name: "", owning_entity_id: "", owning_entity_name: "", owning_entity_type: "" }));
  };

  // Resolve the picked hierarchy entity into stored ownership fields.
  const pickEntity = useCallback((v, item) => {
    setF((s) => {
      if (!v || v === PARENT_ENTITY) {
        return { ...s, subsidiary_id: "", subsidiary_name: "",
          owning_entity_id: s.customer_id, owning_entity_name: s.customer_name, owning_entity_type: "PARENT" };
      }
      const e = item?._item || {};
      return { ...s, subsidiary_id: v, subsidiary_name: item?.label || "",
        owning_entity_id: v, owning_entity_name: item?.label || "",
        owning_entity_type: (e.entity_type || "SUBSIDIARY") };
    });
  }, []);

  const submit = async () => {
    if (!f.customer_id || !f.procured_from_vendor || !f.product_name || !f.warehouse) {
      toast.error("Customer, source vendor, product and warehouse are required"); return;
    }
    setBusy(true);
    try {
      const payload = { ...f, qty: Number(f.qty) || 0, serials: f.tracking_type === "SERIAL" ? f.serials.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [], references: { quotation: f.quotation || null, order: f.order || null, customer_po: f.customer_po || null } };
      const { data } = await api.post("/customer-stock/inward", payload);
      if (data.ids?.length) window.open(`${API}/customer-stock/labels.pdf?ids=${data.ids.join(",")}`, "_blank", "noopener");
      const entity = f.owning_entity_name && f.owning_entity_type !== "PARENT"
        ? ` / ${f.owning_entity_name}` : "";
      toast.success(`Received ${data.created} unit(s) — owner ${f.customer_name || data.owner}${entity}, Suntronix custody`);
      setOpen(false); setCust(f.customer_id); setCustLabel(f.customer_name); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Inward failed"); }
    finally { setBusy(false); }
  };

  const S = summary || {};
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="customer-owned-stock">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-teal-600 grid place-items-center text-white"><Warehouse className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Customer-Owned Stock</h1>
          <p className="text-[13px] text-slate-500">Customer owns it · Suntronix is custodian. No Suntronix inventory value is created.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-56">
            <AsyncCombobox
              value={cust} selectedLabel={custLabel}
              onChange={(id, item) => { setCust(id); setCustLabel(item?.label || ""); }}
              fetcher={searchCustomers}
              placeholder="Filter by customer" emptyText="No customers found."
              testId="cos-customer-filter" />
          </div>
          {cust && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setCust(""); setCustLabel(""); }} data-testid="cos-customer-filter-clear">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Link to="/warehouse/scan" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-300 px-3 text-[12px] font-semibold hover:bg-slate-50" data-testid="cos-scan-link"><ScanLine className="h-4 w-4" />Scan</Link>
        <Button onClick={() => setOpen(true)} data-testid="cos-inward-open" className="bg-teal-600 hover:bg-teal-700"><PackagePlus className="h-4 w-4 mr-1.5" />Receive Customer-Owned Stock</Button>
      </div>
      <div className="text-[11px] text-slate-500" data-testid="cos-quote-hint">Stock arriving under an accepted quotation is received from that quotation (Fulfilment type → Receive &amp; Store). Use the form here only for stock without a Suntronix quotation.</div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="cos-tabs">{TABS.map(([k, l]) => <button key={k} onClick={() => setSp({ tab: k })} className={`-mb-px border-b-2 px-3 py-2 text-[12px] font-semibold ${tab === k ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`cos-tab-${k}`}>{l}</button>)}</div>
      {tab === "board" && <><LabelSettings /><BulkLabels /><CustomerStockBoard /></>}
      {tab === "locations" && <LocationsTab />}
      {tab === "backfill" && <BackfillTab />}
      {tab === "count" && <StockTakeTab />}
      {tab === "search" && <WarehouseSearch />}
      {tab === "stock" && <>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="cos-summary">
        {[["Total", S.total], ["Available", S.AVAILABLE], ["Reserved", S.RESERVED], ["Deployed", S.DEPLOYED], ["Recovery", S.RECOVERY], ["Redeploy Ready", S.REDEPLOYMENT_READY]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`cos-stat-${l}`}>
            <div className="text-[10px] font-medium text-slate-500">{l}</div>
            <div className="text-[20px] font-bold tabular-nums text-slate-900">{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="cos-table">
          <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {["Inventory ID", "Product", "Part No.", "Serial", "Owner", "Source", "Warehouse · Location", "Status", "Label", ""].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">No customer-owned stock. Use “Receive Customer-Owned Stock”.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`cos-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-[11px] font-semibold text-slate-900" data-testid={`cos-code-${r.id}`}><Link to={`/warehouse/scan/${r.code}`} className="hover:underline">{r.code || "—"}</Link></td>
                <td className="px-3 py-2 text-slate-800">{r.product_name}</td>
                <td className="px-3 py-2 text-slate-500">{r.sku || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.serial || `qty ${r.qty}`}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 border border-teal-200 text-teal-700">{r.customer_name || r.master_company_id}</span>
                  {r.owning_entity_name && r.owning_entity_type !== "PARENT" && r.owning_entity_id !== r.master_company_id && (
                    <div className="mt-0.5 text-[10px] text-slate-400">↳ {r.owning_entity_name}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-500">{r.procured_from_vendor}</td>
                <td className="px-3 py-2 text-slate-500">{(r.location_code || [r.zone, r.rack, r.bin].some(Boolean)) ? <span className="font-mono text-[10.5px] text-slate-700">{r.location_code || [r.warehouse, r.zone, r.rack, r.bin].filter(Boolean).join("-")}</span> : (r.warehouse || "—")}</td>
                <td className="px-3 py-2"><span className="text-[10px] font-semibold text-slate-700">{r.status}</span></td>
                <td className="px-3 py-2"><a href={`${API}/customer-stock/labels.pdf?ids=${r.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-50" data-testid={`cos-label-${r.id}`}><Tag className="h-3 w-3" />Label</a></td>
                <td className="px-3 py-2"><RowActions access={access} onEdit={() => setEditU(r)} onDelete={() => setDelU(r)} testid={`cos-${r.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      <UnitEditDialog unit={editU} onClose={() => setEditU(null)} onSaved={() => { setEditU(null); load(); }} />
      <ReasonDialog open={!!delU} title={`Delete ${delU?.code || ""}?`} description={`${delU?.product_name || ""} · ${delU?.serial || ""} — archived with your reason. Allocated, dispatched or deployed units cannot be deleted.`} confirm="Delete (archive)" testid="cos-delete-dialog"
        onClose={() => setDelU(null)} onConfirm={async (reason) => { try { await api.post(`/inventory-admin/units/${delU.id}/archive`, { reason }); toast.success(`${delU.code} archived`); setDelU(null); load(); } catch (e) { toast.error(errMsg(e)); } }} />
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setOpen(false)} data-testid="cos-inward-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg mt-10 p-5">
            <div className="text-sm font-semibold mb-1 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-teal-600" /> Receive Customer-Owned Stock</div>
            <div className="text-[11px] text-slate-500 mb-3">Owner = customer · Custodian = Suntronix · No Suntronix purchase created.</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1 col-span-2"><span className="text-[11px] text-slate-500">Customer / Tenant *</span>
                <AsyncCombobox
                  value={f.customer_id} selectedLabel={f.customer_name}
                  onChange={pickCustomer} fetcher={searchCustomers}
                  placeholder="Search customer by name or code…" emptyText="No customers found."
                  testId="cos-f-customer" />
              </label>
              <div className="col-span-2 space-y-1">
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-slate-500">Subsidiary / Entity</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="text-slate-400 hover:text-slate-600" data-testid="cos-f-subsidiary-info">
                          <Info className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[240px] text-[11px]">
                        Select the subsidiary, child company or legal entity that owns / uses this stock.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <CustomerEntitySelector
                  parentId={f.customer_id} parentName={f.customer_name}
                  value={f.owning_entity_type === "PARENT" ? PARENT_ENTITY : f.subsidiary_id}
                  valueLabel={f.owning_entity_name}
                  onChange={pickEntity}
                  testId="cos-f-subsidiary" />
              </div>
              {[["procured_from_vendor", "Procured from (source vendor) *"], ["product_name", "Product *"], ["brand", "Brand"], ["sku", "SKU / OEM part #"], ["warehouse", "Warehouse *"], ["zone", "Zone"], ["rack", "Rack"], ["bin", "Bin"], ["quotation", "Quote (STX-Q-…)"], ["order", "Order (STX-ORD-…)"], ["customer_po", "Customer PO"]].map(([k, l]) => (
                <label key={k} className="space-y-1"><span className="text-[11px] text-slate-500">{l}</span>
                  <Input value={f[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`cos-f-${k}`} /></label>
              ))}
              <label className="space-y-1"><span className="text-[11px] text-slate-500">Tracking</span>
                <select value={f.tracking_type} onChange={(e) => upd("tracking_type", e.target.value)} data-testid="cos-f-tracking" className="w-full h-9 rounded-md border border-slate-200 px-2 text-[13px]"><option value="SERIAL">Serialised</option><option value="QTY">Quantity</option></select></label>
              <label className="space-y-1"><span className="text-[11px] text-slate-500">Condition</span>
                <select value={f.condition} onChange={(e) => upd("condition", e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-2 text-[13px]">{["NEW", "OPEN_BOX", "USED", "REFURBISHED", "DAMAGED", "QUARANTINE"].map((c) => <option key={c}>{c}</option>)}</select></label>
              {f.tracking_type === "SERIAL" ? (
                <label className="space-y-1 col-span-2"><span className="text-[11px] text-slate-500">Serials (one per line or comma)</span>
                  <textarea value={f.serials} onChange={(e) => upd("serials", e.target.value)} data-testid="cos-f-serials" rows={4} className="w-full rounded-md border border-slate-200 p-2 text-[13px] font-mono" /></label>
              ) : (
                <label className="space-y-1"><span className="text-[11px] text-slate-500">Quantity *</span>
                  <Input type="number" value={f.qty} onChange={(e) => upd("qty", e.target.value)} data-testid="cos-f-qty" /></label>
              )}
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="cos-inward-submit" className="bg-teal-600 hover:bg-teal-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post Inward"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
